import { useState } from 'react';
import { AppFormDialog, AppPickerRow, AppSearchField } from '@core/designsystem';
import { NewClientDialog, type ClientDraft } from '@core/ui/NewClientDialog';
import type { ClientOption } from './types';

/**
 * Выбор клиента с поиском — и заведение нового прямо отсюда.
 *
 * Поиск есть **независимо от числа вариантов** ([D-31](docs/DECISIONS.md)): юрист
 * с тремя клиентами не знает, что поиск появится потом, и видит просто маленький
 * список без него.
 *
 * **«Новый клиент» стоит первым пунктом, а не последним.** Дело заводят на клиента,
 * которого в приложении ещё нет, — это обычный случай, а не исключение. Пока завести
 * его отсюда было нельзя, юрист уходил в раздел клиентов и не возвращался: дело
 * оставалось незаведённым. Ровно это уже проходили на Android.
 */
export function ClientPicker({
  clients,
  onDismiss,
  onSelect,
  onCreate,
  clearLabel = 'Без клиента',
  creatable = true,
  title = 'Клиент',
}: {
  clients: ClientOption[];
  onDismiss: () => void;
  onSelect: (id: string | null) => void;
  onCreate: (draft: ClientDraft) => void;
  /** Как назвать «снять клиента»: при создании — «Без клиента», в деле — «Убрать». */
  clearLabel?: string;
  /** Можно ли завести клиента отсюда. В отборе списка дел заводить нечего. */
  creatable?: boolean;
  /** Заголовок: «Клиент» при выборе, «Дела клиента» при отборе списка. */
  title?: string;
}) {
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  const found = clients.filter((client) =>
    client.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  if (adding) {
    return <NewClientDialog onDismiss={() => setAdding(false)} onCreate={onCreate} />;
  }

  return (
    <AppFormDialog title={title} onDismiss={onDismiss}>
      <AppSearchField value={query} onChange={setQuery} placeholder="Поиск клиента" />

      <div className="dialog__list">
        {creatable ? (
          <AppPickerRow label="Новый клиент" value="" action onClick={() => setAdding(true)} />
        ) : null}
        <AppPickerRow label={clearLabel} value="" onClick={() => onSelect(null)} />
        {found.map((client) => (
          <AppPickerRow
            key={client.id}
            label={client.name}
            value=""
            onClick={() => onSelect(client.id)}
          />
        ))}
      </div>
    </AppFormDialog>
  );
}
